import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Link2, Users, LogOut, PlusCircle, UserCircle2, Menu, X, ShieldCheck, Gift, DollarSign, History, Youtube, Webhook, ShoppingBag } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
  { to: "/dashboard/links", label: "Links", icon: Link2, testid: "nav-links" },
  { to: "/dashboard/shares", label: "Recent shares", icon: ShoppingBag, testid: "nav-shares" },
  { to: "/dashboard/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/dashboard/earnings", label: "My commissions", icon: DollarSign, testid: "nav-earnings" },
  { to: "/dashboard/payouts", label: "Payout history", icon: History, testid: "nav-payouts" },
  { to: "/dashboard/profile", label: "Profile", icon: UserCircle2, testid: "nav-profile" },
];

const adminNavItems = [
  { to: "/dashboard/admin", label: "Search & accounts", icon: ShieldCheck, testid: "nav-admin" },
  { to: "/dashboard/admin/payments", label: "Payments", icon: DollarSign, testid: "nav-admin-payments" },
  { to: "/dashboard/admin/landing-videos", label: "Landing videos", icon: Youtube, testid: "nav-admin-videos" },
  { to: "/dashboard/admin/integrations", label: "Integrations", icon: Webhook, testid: "nav-admin-integrations" },
];

function NavBody({ user, onLogout, onItemClick }) {
  const [sources, setSources] = useState([]);
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/sources");
        setSources(Array.isArray(data) ? data : []);
      } catch {
        setSources([]);
      }
    })();
  }, [user]);
  return (
    <>
      <div className="px-6 py-6 border-b border-[#E5E7EB]">
        <Link to="/" className="block" onClick={onItemClick}>
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-[#111827] flex items-center justify-center">
              <span className="font-display font-black text-white text-sm">T</span>
            </div>
            <div>
              <div className="font-display font-black tracking-tight text-[#111827]">TRACE</div>
              <div className="label-eyebrow text-[#4B5563]">Affiliate Tracker</div>
            </div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <NavLink
          to="/dashboard/links/new"
          data-testid="nav-create-link"
          onClick={onItemClick}
          className="flex items-center gap-2 rounded-md bg-[#002FA7] text-white px-3 py-2 text-sm font-bold hover:bg-[#001f70] transition-colors mb-3"
        >
          <PlusCircle size={16} /> New link
        </NavLink>
        {navItems.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            data-testid={it.testid}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                isActive
                  ? "bg-[#111827] text-white"
                  : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#111827]"
              }`
            }
          >
            <it.icon size={16} />
            {it.label}
          </NavLink>
        ))}

        {sources.length > 0 && (
          <>
            <div className="label-eyebrow text-[#9CA3AF] px-3 pt-5 pb-2">Sources</div>
            {sources.map((s) => (
              <NavLink
                key={s.source}
                to={`/dashboard/source/${encodeURIComponent(s.source)}`}
                onClick={onItemClick}
                data-testid={`nav-source-${s.source}`}
                className={({ isActive }) =>
                  `flex items-center justify-between gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#111827] text-white"
                      : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#111827]"
                  }`
                }
              >
                <span className="inline-flex items-center gap-3 min-w-0">
                  <ShoppingBag size={16} className="flex-shrink-0" />
                  <span className="truncate">{s.source}</span>
                </span>
                {s.conversion_count > 0 && (
                  <span className="text-[10px] font-bold tabular-nums opacity-70">{s.conversion_count}</span>
                )}
              </NavLink>
            ))}
          </>
        )}

        {user?.role === "admin" && (
          <>
            <div className="label-eyebrow text-[#9CA3AF] px-3 pt-5 pb-2">Admin</div>
            {adminNavItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                onClick={onItemClick}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#002FA7] text-white"
                      : "text-[#4B5563] hover:bg-[#EFF6FF] hover:text-[#002FA7]"
                  }`
                }
              >
                <it.icon size={16} />
                {it.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="px-4 py-4 border-t border-[#E5E7EB]">
        <div className="flex items-center gap-3 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="h-9 w-9 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-bold text-[#111827] truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-[#4B5563] truncate">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="w-full inline-flex items-center justify-center gap-2 text-xs font-bold tracking-wider uppercase text-[#4B5563] hover:text-[#111827] transition-colors py-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = original; };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-white border-r border-[#E5E7EB] hidden md:flex flex-col" data-testid="sidebar">
        <NavBody user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 bg-white border-b border-[#E5E7EB] z-30 px-4 py-3 flex items-center justify-between" data-testid="mobile-topbar">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-md bg-[#111827] flex items-center justify-center">
            <span className="font-display font-black text-white text-xs">T</span>
          </div>
          <span className="font-display font-black text-[#111827]">TRACE</span>
        </Link>
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-button"
          aria-label="Open menu"
          className="inline-flex items-center justify-center h-10 w-10 rounded-md border border-[#E5E7EB] text-[#111827] hover:bg-[#F9FAFB] transition-colors"
        >
          <Menu size={18} />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/40 z-40 fade-up"
            data-testid="mobile-drawer-overlay"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="md:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white z-50 flex flex-col shadow-2xl"
            data-testid="mobile-drawer"
            style={{ animation: "fadeUp 0.18s ease-out" }}
          >
            <button
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-drawer-close"
              aria-label="Close menu"
              className="absolute top-3 right-3 h-9 w-9 rounded-md inline-flex items-center justify-center text-[#4B5563] hover:bg-[#F3F4F6] z-10"
            >
              <X size={18} />
            </button>
            <NavBody user={user} onLogout={handleLogout} onItemClick={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 md:ml-0 mt-14 md:mt-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 md:py-10">{children}</div>
      </main>
    </div>
  );
}
